// documented in: docs/overview/subsystems/auth_system.md (frontend auth actions with localStorage persistence)
import { useDispatch } from "react-redux";
import { setAuth, clearAuth } from "@store/slices/auth";
import { LocalStorage } from "@/types/enums";
import { RouteTo } from "@routing/RouteMap";
import { useNavigate } from "react-router-dom";
import { showInfoNotification, showSuccessNotification } from "@utils/errorHandling";
import { AuthPayload } from "@/app/graphql/codegen";
import client from "@/app/graphql/client";
import { trackLogoutNavigation } from "@/utils/logoutTracker";

/**
 * Bündelt Redux-Aktionen und side effects (localStorage und navigation) für die Authentifizierung.
 */
const useAuthActions = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const login = (authPayload: AuthPayload, route = true) => {

        dispatch(setAuth(authPayload));

        // Ensure Apollo cache is in sync with new authentication state
        // We intentionally do not await to keep UI snappy
        void client.resetStore();

        // Synchronize with localStorage.
        localStorage.setItem(LocalStorage.AUTH_TOKEN, authPayload.authToken);
        localStorage.setItem(LocalStorage.USER_ID, authPayload.id);
        localStorage.setItem(LocalStorage.USER_EMAIL, authPayload.email);

        // Erfolgsbenachrichtigung anzeigen
        showSuccessNotification(
            "Sie wurden erfolgreich angemeldet", 
            dispatch, 
            "Erfolg"
        );

        if (route) navigate(RouteTo.DASHBOARD);
    };

    const logout = (route = true, skipNotification = false) => {
        console.log("🚀 Starting logout process", { route, skipNotification });
        
        dispatch(clearAuth());
        void client.resetStore();

        // Clear authentication data from localStorage.
        localStorage.removeItem(LocalStorage.AUTH_TOKEN);
        localStorage.removeItem(LocalStorage.USER_ID);
        localStorage.removeItem(LocalStorage.USER_EMAIL);

        // Show notification unless explicitly skipped (useful for auth error scenarios)
        if (!skipNotification) {
            showInfoNotification(
                "Sie wurden erfolgreich abgemeldet", 
                dispatch, 
                "Abgemeldet"
            );
        }

        if (route) {
            // Track navigation for logout success monitoring
            trackLogoutNavigation();
            navigate(RouteTo.LOGIN);
            console.log("✅ Logout navigation initiated");
        }
    };

    return { login, logout };
};

export default useAuthActions;
